import Vue from 'vue'
import VueRouter from 'vue-router'
import { getMenuList } from '@/api'

Vue.use(VueRouter)

// 静态路由（公开页面）
const staticRoutes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { public: true }
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/Register.vue'),
    meta: { public: true }
  }
]

// 默认首页路由（动态加载后会被覆盖）
// 优先使用本地缓存的自定义首页
let defaultHomePath = localStorage.getItem('customHomePath') || '/trade/signal-preview'

// 404 路由
const notFoundRoute = {
  path: '*',
  redirect: () => defaultHomePath
}

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: staticRoutes
})

// 解决 Vue Router 重复导航报错问题
const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => {
    if (err.name !== 'NavigationDuplicated' &&
        !err.message.includes('Redirected when going from')) {
      throw err
    }
  })
}

// 动态路由是否已加载
let dynamicRoutesLoaded = false

// 加载动态路由
async function loadDynamicRoutes() {
  if (dynamicRoutesLoaded) return true

  try {
    const res = await getMenuList()
    const menus = res.data || []

    if (menus.length === 0) {
      console.warn('No menus found')
      return false
    }

    // 将菜单转换为路由（过滤掉 Layout 等非页面组件）
    const dynamicRoutes = menus
      .filter(menu => menu.component && menu.component !== 'Layout')
      .map(menu => ({
        path: menu.path,
        name: menu.component,
        component: () => import(`@/views/${menu.component}.vue`),
        meta: { title: menu.name, icon: menu.icon }
      }))

    // 设置默认首页：优先使用本地缓存，否则使用第一个有效路由
    const customHome = localStorage.getItem('customHomePath')
    if (customHome && dynamicRoutes.some(r => r.path === customHome)) {
      defaultHomePath = customHome
    } else if (dynamicRoutes.length > 0) {
      defaultHomePath = dynamicRoutes[0].path
    }

    // 添加首页重定向
    router.addRoute({
      path: '/',
      redirect: defaultHomePath
    })

    // 添加动态路由
    dynamicRoutes.forEach(route => {
      router.addRoute(route)
    })

    // 添加 404 路由（必须最后添加）
    router.addRoute(notFoundRoute)

    dynamicRoutesLoaded = true

    // 存储菜单到 localStorage 供侧边栏使用
    localStorage.setItem('menus', JSON.stringify(menus))

    return true
  } catch (e) {
    console.error('Failed to load dynamic routes:', e)
    return false
  }
}

// 重置动态路由（登出时调用）
function resetDynamicRoutes() {
  dynamicRoutesLoaded = false
  localStorage.removeItem('menus')
}

// 路由守卫
router.beforeEach(async (to, from, next) => {
  // 公开页面直接放行
  if (to.meta.public) {
    next()
    return
  }

  // 检查 Token
  const token = localStorage.getItem('token')
  const tokenExpire = localStorage.getItem('tokenExpire')

  if (!token) {
    next({ path: '/login', query: { redirect: to.fullPath } })
    return
  }

  // 检查 Token 是否过期
  if (tokenExpire && Date.now() > parseInt(tokenExpire)) {
    localStorage.removeItem('token')
    localStorage.removeItem('username')
    localStorage.removeItem('tokenExpire')
    resetDynamicRoutes()
    next({ path: '/login', query: { redirect: to.fullPath } })
    return
  }

  // 加载动态路由
  if (!dynamicRoutesLoaded) {
    const loaded = await loadDynamicRoutes()
    if (loaded) {
      // 重新导航到目标路由
      next({ ...to, replace: true })
      return
    }
  }

  next()
})

// 路由切换后更新浏览器标题
router.afterEach((to) => {
  const title = to.meta && to.meta.title
  document.title = title ? `${title} - QuantMid` : 'QuantMid Dashboard'
})

export { resetDynamicRoutes, loadDynamicRoutes }
export default router
